import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useContext(AuthContext);
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [productName, setProductName] = useState('');
  const [productType, setProductType] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const product = searchParams.get('product');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('Invalid checkout session');
      return;
    }

    pollPaymentStatus(sessionId, product);
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, product, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage('Payment verification timed out. Please check your account or contact support.');
      return;
    }

    try {
      const response = await axios.get(`${API}/billing/checkout-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setMessage(data.message || 'Payment successful!');
        setProductName(data.product_name || product);
        setProductType(data.product_type || product);
        
        // Refresh user data
        await fetchUser();
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, product, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, product, attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setMessage('Error verifying payment. Please contact Admin@dailyuaejobs.com or WhatsApp +971 52 131 0804.');
      }
    }
  };

  const getNextAction = () => {
    switch (productType) {
      case 'cv_fix':
        return { text: 'Fix My CV Now', link: '/cv-checker' };
      case 'interview_pack':
        return { text: 'Open Interview Pack', link: '/interview-pack' };
      case 'job_apply_kit':
        return { text: 'Open Job Apply Kit', link: '/job-apply-kit' };
      case 'pro_subscription':
        return { text: 'Go to Dashboard', link: '/dashboard' };
      default:
        return { text: 'Go to Dashboard', link: '/dashboard' };
    }
  };

  const nextAction = getNextAction();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <Card className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-[#CE1126] mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold mb-4">Processing Your Payment...</h1>
              <p className="text-gray-600">Please wait while we verify your payment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-[#009639] bg-opacity-10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-[#009639]" />
              </div>
              <h1 className="text-3xl font-bold mb-4 text-[#009639]">Payment Successful!</h1>
              <p className="text-xl text-gray-700 mb-2">{productName}</p>
              <p className="text-gray-600 mb-8">{message}</p>
              
              <div className="space-y-4">
                <Link to={nextAction.link}>
                  <Button className="btn-uae-red px-8 py-3 text-lg">
                    {nextAction.text}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                
                <div className="text-sm text-gray-500">
                  <p>A confirmation email has been sent to your inbox.</p>
                </div>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold mb-4 text-red-600">Payment Issue</h1>
              <p className="text-gray-600 mb-8">{message}</p>
              
              <div className="space-y-4">
                <Link to="/pricing">
                  <Button variant="outline" className="px-8">
                    Try Again
                  </Button>
                </Link>
                <p className="text-sm text-gray-500">
                  Need help? <Link to="/contact" className="text-[#CE1126] hover:underline">Contact Support</Link>
                </p>
              </div>
            </>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
}
